import React, { useEffect, useState } from "react";
import { Table, Button, Input, Modal, Space, message, Form, Select, InputNumber, Popconfirm, Flex } from "antd";
import { ColumnType } from "antd/es/table";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Editor } from "@monaco-editor/react";
import StringUtils from "../../../../../../util/StringUtils";
import DeviceTemplateApi from "../../../../../../api/DeviceTemplateApi";
import ProductApi from "../../../../../../api/ProductApi";
import { DeviceServicesWrapper, EditServiceFormLayout, EditServiceFormRow, EditServiceParamFormLayout, EditServiceParamFormRow, EditServiceParamWrapper, EditServiceWrapper, OperationWrapper, ScriptEditorContainer, ServiceParamCell, ServiceParamRow, ServiceParamRowTitle, ServiceParamsTableWrapper, TableWrapper, Title, TitleWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./DeviceServices.styled";

interface DeviceServiceProps {
    deviceTemplateView: any
}

let DeviceServices: React.FC<DeviceServiceProps> = (props)=> {
    // params   
    let deviceTemplateView = props.deviceTemplateView;
    let configurationJson = deviceTemplateView.deviceTemplate.configuration;
    let configuration = JSON.parse(configurationJson ? configurationJson : '{}');
    if (configuration.services == null) {
        configuration.services = [];
    }
    
    //api
    const [messageApi, contextHolder] = message.useMessage();
    let deviceTemplateApi = new DeviceTemplateApi();
    let productApi = new ProductApi();

    // state
    let [openEditServiceModal, setOpenEditServiceModal] = useState(false);
    let [editingService, setEditingService] = useState(null as any);
    let [editServiceMode, setEditServiceMode] = useState(null as any);
    let [product, setProduct] = useState(null as any);
    let [services, setServices] = useState(configuration.services);
    let [search, setSearch] = useState('');

    // functions
    let saveServices = ()=> {
        // re-render
        setServices([].concat(services));

        // update configuration
        let configurationJson = deviceTemplateView.deviceTemplate.configuration;
        let configuration = JSON.parse(configurationJson ? configurationJson : '{}');
        configuration.services = services;
        deviceTemplateView.deviceTemplate.configuration = JSON.stringify(configuration);

        // do save
        deviceTemplateApi.saveConfiguration(deviceTemplateView.deviceTemplate.id, configuration);

        // show success
        messageApi.open({
            type: 'success',
            content: '保存成功',
        });
    };

    let editService = (service)=> {
        let serviceIndex = services.indexOf(editingService);
        if (serviceIndex < 0) {
            return;
        }
        services[serviceIndex] = service;
        saveServices();
    };

    let addService = (service)=> {
        services.push(service);
        saveServices();
    };

    let getFilterServices = ()=> {
        if (!search) {
            return services;
        }
        let filterServices = services.filter((e)=> {
            return e.name.indexOf(search) >=0 || e.key.indexOf(search) >= 0;
        });
        return filterServices;
    };

    let onAddService = ()=> {
        setEditServiceMode('CREATE');
        setEditingService(null);
        setOpenEditServiceModal(true);
    };

    let onEditService = (service)=> {
        setEditServiceMode('EDIT');
        setEditingService(service);
        setOpenEditServiceModal(true);
    }

    let onDeleteService = (service)=> {
        let index = services.indexOf(service);
        if (index < 0) {
            return;
        }
        services.splice(index, 1);
        saveServices();
    };

    let onSaveService = (service)=> {
        if (editServiceMode == 'CREATE') {
            addService(service);
        } else {
            editService(service);
        }
    };

    let onSearchChange = (e)=> {
        setSearch(e.target.value);
    };

    let onMoveUp = (service)=> {
        let serviceIndex = services.indexOf(service);
        if (serviceIndex <= 0) {
            return;
        }
        services[serviceIndex] = services[serviceIndex-1];
        services[serviceIndex-1] = service;
        setServices([].concat(services));
        saveServices();
    }

    let onMoveDown = (service)=> {
        let serviceIndex = services.indexOf(service);
        if (serviceIndex < 0 || serviceIndex >= services.length) {
            return;
        }
        services[serviceIndex] = services[serviceIndex+1];
        services[serviceIndex+1] = service;
        setServices([].concat(services));
        saveServices();
    }

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '服务名称',
            dataIndex: 'service.name',
            key: 'service.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.name}</span>
            }
        },
        {
            title: '服务标识',
            dataIndex: 'service.key',
            key: 'service.key',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.key}</span>
            }
        },
        {
            title: '设备服务',
            dataIndex: 'service.registerAddress',
            key: 'service.registerAddress',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.registerAddress}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'service.remarks',
            key: 'service.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> onEditService(record)}>编辑服务</a>
                    <Popconfirm
                            title="删除服务"
                            description={ <div style={{padding: '10px 40px 10px 0px'}}>确认要删除服务么?</div> }
                            onConfirm={()=> onDeleteService(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除服务</a>
                    </Popconfirm>
                    <a onClick={()=> onMoveUp(record)}>上移</a>
                    <a onClick={()=> onMoveDown(record)}>下移</a>
                </Space>
            }
        }
    ];

    // effect
    useEffect(()=> {
        let loadProduct = async ()=> {
            let product = (await productApi.getProductByModel(deviceTemplateView.deviceTemplate.productModel)).data.data;
            setProduct(product);
        };
        loadProduct();
    }, []);

    // render
    return <DeviceServicesWrapper>
        {contextHolder}

        <ToolBarWrapper>
            <ToolBarLeftWrapper>
                <Space size="middle">
                    <Button type="primary" icon={<PlusOutlined />} onClick={onAddService}>添加服务</Button>
                </Space>
            </ToolBarLeftWrapper>

            <ToolBarRightWrapper>
                <Input placeholder="请输入服务名称" suffix={<SearchOutlined />} size="large" onChange={onSearchChange} />
            </ToolBarRightWrapper>

            <Modal open={openEditServiceModal} 
                onClose={()=> setOpenEditServiceModal(false)} 
                footer={null} 
                closable={false}
                destroyOnClose={true}
                width={800}
                height={600}
                >
                <EditService 
                    product={product}
                    deviceTemplate={deviceTemplateView.deviceTemplate}
                    service={editingService}
                    create={editServiceMode == 'CREATE' ? true : false}
                    onSave={(service)=> onSaveService(service)}
                    onClose={()=> setOpenEditServiceModal(false)}
                    /> 
            </Modal>
        </ToolBarWrapper>
        <TableWrapper>
            <Table
                rowKey={(record)=> record.name}
                columns={columns} 
                dataSource={getFilterServices()}
            />
        </TableWrapper>

    </DeviceServicesWrapper>
};

interface EditServiceProps {
    product: any,
    deviceTemplate: any,
    service: any,
    create: boolean;

    onClose: ()=> void,
    onSave: (service: any)=> void,
}

let EditService: React.FC<EditServiceProps> = (props)=> {
    const deviceTemplate = props.deviceTemplate;
    const product = props.product;
    const [form] = Form.useForm();

    let specificationJson = product.specification;
    let specification = JSON.parse(specificationJson ? specificationJson : '{}');
    if (specification.services == null) {
        specification.services = [];
    }
    let mappingServiceOptions = specification.services.map((service)=> {
        return {
            label: service.name,
            value: service.key,
        };
    });
    
    let mappingTypeOptions = [
        {label: '映射功能', value: 'MAPPING'},
        {label: '自定义脚本', value: 'SCRIPT'},
    ];

    // state
    let [service, setService] = useState(props.service ?? {});
    let [canSave, setCanSave] = useState(false);
    let [editingServiceParam, setEditingServiceParam] = useState<any>({});
    let [editingServiecParamDirction, setEditingServiceDirection] = useState<any>({});
    let [openEditServiceParamModal, setOpenEditServiceParamModal] = useState(false);

    // computed
    let mappingService = specification.services.find((e)=> e.key == service.mappingService);

    // validate
    let validateForm = ()=> {
        setService({...service});
        form.validateFields().then((values)=> {
            setCanSave(true);
        }).catch((err)=> {
            setCanSave(false);
        });
    }

    // events
    let onSave = (e)=> {
        props.onSave(service);
        props.onClose();
    };

    let onCancel = (e)=> {
        props.onClose();
    };

    let onServiceNameChange = (e)=> {
        service.name = e.target.value;
        validateForm();
    };

    let onServiceKeyChange = (e)=> {
        service.key = e.target.value;
        validateForm();
    };

    let onRemarksChange = (e)=> {
        service.remarks = e.target.value;
        validateForm();
    };
    
    let serviceNameValidator = (rule, value, callback)=> {
        let nameDuplicated = false;
        let services = deviceTemplate.services;
        if (services == null) {
            services = [];
        }
        for(let existingService of services) {
            if (existingService == service) {
                continue;
            }
            if (existingService.name == value) {
                nameDuplicated = true;
                break;
            }
        }

        if (nameDuplicated) {
            callback("服务名称重复");
        } else {
            callback();
        }
    };
        
    let serviceKeyValidator = (rule, value, callback)=> {
        let nameDuplicated = false;
        let services = deviceTemplate.services;
        if (services == null) {
            services = [];
        }
        for(let existingService of services) {
            if (existingService == service) {
                continue;
            }
            if (existingService.key == value) {
                nameDuplicated = true;
                break;
            }
        }

        if (nameDuplicated) {
            callback("服务标识重复");
        } else {
            callback();
        }
    };

    let onEditServiceInParamClick = (paramIndex)=> {
        let param = service.inParams[paramIndex];
        setEditingServiceParam(param);
        setEditingServiceDirection('IN');
        setOpenEditServiceParamModal(true);
    };

    let onEditServiceOutParamClick = (paramIndex)=> {
        let param = service.outParams[paramIndex];
        setEditingServiceParam(param);
        setEditingServiceDirection('OUT');
        setOpenEditServiceParamModal(true);
    };

    let onAddServiceInParamClick = (e)=> {
        setOpenEditServiceParamModal(true);
    };

    let onAddServiceOutParamClick = (e)=> {
        setOpenEditServiceParamModal(true);
    };

    let onDeleteServiceInParamClick = (paramIndex)=> {
        service.inParams = service.inParams?.filter((param, index)=> {
            return paramIndex != index;
        });
        setService({...service});
    };

    let onDeleteServiceOutParamClick = (paramIndex)=> {
        service.outParams = service.outParams?.filter((param, index)=> {
            return paramIndex != index;
        });
        setService({...service});
    };

    let onSaveServiceParam = (param)=> {
        if (editingServiceParam == null) {
            if (editingServiecParamDirction == 'IN') {
                service.inParams = service.inParams ?? [];
                service.inParams.push(param);
            } else if(editingServiecParamDirction == 'OUT') {
                service.outParams = service.outParams ?? [];
                service.outParams.push(param);
            }
        }
        setService({...service});
    };

    let onMappingTypeChange = (mappingType)=> {
        service.mappingType = mappingType;
        setService({...service});
    };

    let onMappingServiceChange = (mappingServiceKey)=> {
        service.mappingService = service.mappingService ?? {}
        service.mappingService.key = mappingServiceKey;
        setService({...service});
    };

    let onInParamMappingChange = (mappingServiceParamKey: string, serviceParamKey: string)=> {
        service.mappingService = service.mappingService ?? {}
        service.mappingService.inParams[mappingServiceParamKey] = serviceParamKey;
        setService({...service});
    };

    let onOutParamMappingChange = (mappingServiceParamKey: string, serviceParamKey: string)=> {
        service.mappingService = service.mappingService ?? {}
        service.mappingService.outParams[mappingServiceParamKey] = serviceParamKey;
        setService({...service});
    };

    let onScriptEditorMount = (editor: any, monaco: any) => {
        // nop
    };

    let onScriptEditorChange = (value: string) => {
        if (value !== undefined) {
            service.mappingScript = value;
            setService({...service});
        }
    };

    // effect
    useEffect(()=> {
        setService(props.service ?? {});
    }, [props.service, props.create]);

    // render
    return (
        <EditServiceWrapper>
            <TitleWrapper>
                <Title>{ props.create ? '新增服务' : '编辑服务'}</Title>
            </TitleWrapper>

            <Form form={form} name="form" layout="vertical">
                <EditServiceFormLayout>
                    <EditServiceFormRow>
                        <Form.Item name="name" label="服务名称" rules={[{required: true}, {validator: serviceNameValidator}]} initialValue={service.name}>
                            <Input onChange={onServiceNameChange} />
                        </Form.Item>

                        <Form.Item name="key" label="服务标识" rules={[{required: true}, {validator: serviceKeyValidator}]} initialValue={service.key}>
                            <Input onChange={onServiceKeyChange} />
                        </Form.Item>
                    </EditServiceFormRow>

                    <EditServiceFormRow>
                        <Form.Item name="inParams" label="输入参数">
                            <ServiceParamsTableWrapper>
                                <ServiceParamRowTitle>
                                    <ServiceParamCell>参数名称</ServiceParamCell>
                                    <ServiceParamCell>参数类型</ServiceParamCell>
                                    <ServiceParamCell>参数范围</ServiceParamCell>
                                    <ServiceParamCell>备注</ServiceParamCell>
                                    <ServiceParamCell>操作</ServiceParamCell>
                                </ServiceParamRowTitle>

                                {
                                    (service.inParams ?? []).map((param, index)=> {
                                        return (
                                            <ServiceParamRow key={index}>
                                                <ServiceParamCell>{param.name}</ServiceParamCell>
                                                <ServiceParamCell>{param.type}</ServiceParamCell>
                                                <ServiceParamCell>{param.range}</ServiceParamCell>
                                                <ServiceParamCell>{param.remarks}</ServiceParamCell>
                                                <ServiceParamCell>
                                                    <Button type="link" onClick={()=> onEditServiceInParamClick(index)}>编辑</Button>
                                                    <Button type="link" onClick={()=> onDeleteServiceInParamClick(index)}>删除</Button>
                                                </ServiceParamCell>
                                            </ServiceParamRow>
                                        );
                                    })
                                }

                                <ServiceParamRow>
                                    <Button type="link" icon={<PlusOutlined />} onClick={onAddServiceInParamClick}>添加参数</Button>
                                </ServiceParamRow>
                            </ServiceParamsTableWrapper>
                        </Form.Item>
                    </EditServiceFormRow>
                    
                    <EditServiceFormRow>
                        <Form.Item name="inParams" label="输出参数">
                            <ServiceParamsTableWrapper>
                                <ServiceParamRowTitle>
                                    <ServiceParamCell>参数名称</ServiceParamCell>
                                    <ServiceParamCell>参数类型</ServiceParamCell>
                                    <ServiceParamCell>参数范围</ServiceParamCell>
                                    <ServiceParamCell>备注</ServiceParamCell>
                                    <ServiceParamCell>操作</ServiceParamCell>
                                </ServiceParamRowTitle>

                                {
                                    (service.outParams ?? []).map((param, index)=> {
                                        return (
                                            <ServiceParamRow key={index}>
                                                <ServiceParamCell>{param.name}</ServiceParamCell>
                                                <ServiceParamCell>{param.type}</ServiceParamCell>
                                                <ServiceParamCell>{param.range}</ServiceParamCell>
                                                <ServiceParamCell>{param.remarks}</ServiceParamCell>
                                                <ServiceParamCell>
                                                    <Button type="link" onClick={()=> onEditServiceOutParamClick(index)}>编辑</Button>
                                                    <Button type="link" onClick={()=> onDeleteServiceOutParamClick(index)}>删除</Button>
                                                </ServiceParamCell>
                                            </ServiceParamRow>
                                        );
                                    })
                                }
                                <ServiceParamRow>
                                    <Button type="link" icon={<PlusOutlined />} onClick={onAddServiceOutParamClick}>添加参数</Button>
                                </ServiceParamRow>
                            </ServiceParamsTableWrapper>
                        </Form.Item>
                    </EditServiceFormRow>

                    <EditServiceFormRow>
                        <Form.Item name="mappingType" label="映射方式" rules={[{required: false}]} initialValue={service.mappingType}>
                            <Select
                                placeholder="请选择映射方式"
                                onChange={onMappingTypeChange}
                                options={mappingTypeOptions}
                            />
                        </Form.Item>
                        <Form.Item name="mappingService" label="映射功能" rules={[{required: false}]} initialValue={service.mappingService?.key}>
                            <Select
                                placeholder="请选择映射功能"
                                disabled={service.mappingType != 'MAPPING'}
                                onChange={onMappingServiceChange}
                                options={mappingServiceOptions}
                            />
                        </Form.Item>
                    </EditServiceFormRow>

                    { service.mappingType == 'MAPPING' && <EditServiceFormRow>
                        <Form.Item name="inParamMappings" label="输入参数映射" rules={[{required: false}]}>
                            <ServiceParamsTableWrapper>
                                <ServiceParamRowTitle>
                                    <ServiceParamCell>参数名称</ServiceParamCell>
                                    <ServiceParamCell>参数类型</ServiceParamCell>
                                    <ServiceParamCell>参数绑定</ServiceParamCell>
                                </ServiceParamRowTitle>

                                {
                                    (mappingService?.inParams ?? []).map((param, index)=> {
                                        return (
                                            <ServiceParamRow key={index}>
                                                <ServiceParamCell>{param.name}</ServiceParamCell>
                                                <ServiceParamCell>{param.type}</ServiceParamCell>
                                                <ServiceParamCell>
                                                    <Select 
                                                        placeholder="请选择参数" 
                                                        onChange={(value)=> onInParamMappingChange(param.key, value)} 
                                                        options={service.inParams?.map((p)=>({label: p.name, value: p.key}))}
                                                        value={service.inParamMappings?.[param.key]}
                                                    />
                                                </ServiceParamCell>
                                            </ServiceParamRow>
                                        );
                                    })
                                }
                            </ServiceParamsTableWrapper>
                        </Form.Item>
                    </EditServiceFormRow> }

                    { service.mappingType == 'MAPPING' && <EditServiceFormRow>
                        <Form.Item name="mappingParams" label="输出参数映射" rules={[{required: false}]}>
                            <ServiceParamsTableWrapper>
                                <ServiceParamRowTitle>
                                    <ServiceParamCell>参数名称</ServiceParamCell>
                                    <ServiceParamCell>参数类型</ServiceParamCell>
                                    <ServiceParamCell>参数绑定</ServiceParamCell>
                                </ServiceParamRowTitle>

                                {
                                    (mappingService?.outParams ?? []).map((param, index)=> {
                                        return (
                                            <ServiceParamRow key={index}>
                                                <ServiceParamCell>{param.name}</ServiceParamCell>
                                                <ServiceParamCell>{param.type}</ServiceParamCell>
                                                <ServiceParamCell>
                                                    <Select 
                                                        placeholder="请选择参数" 
                                                        onChange={(value)=> onOutParamMappingChange(param.key, value)} 
                                                        options={service.outParams?.map((p)=>({label: p.name, value: p.key}))}
                                                        value={service.mappingOutParams?.[param.key]}
                                                    />
                                                </ServiceParamCell>
                                            </ServiceParamRow>
                                        );
                                    })
                                }
                            </ServiceParamsTableWrapper>
                        </Form.Item>
                    </EditServiceFormRow> }

                    { service.mappingType == 'SCRIPT' && 
                    <ScriptEditorContainer>
                        <Editor
                            height="100%"
                            defaultLanguage="javascript"
                            value={service.mappingScript}
                            onMount={onScriptEditorMount}
                            onChange={onScriptEditorChange}
                            theme="vs-dark"
                            options={{
                                minimap: { enabled: false },
                                fontSize: 13,
                                fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                                lineNumbers: 'on',
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 4,
                                insertSpaces: true,
                                wordWrap: 'on',
                                folding: true,
                                renderWhitespace: 'selection',
                                bracketPairColorization: { enabled: true },
                                suggestOnTriggerCharacters: true,
                                quickSuggestions: true,
                                parameterHints: true
                            }}
                        />
                    </ScriptEditorContainer>
                    }

                    <EditServiceFormRow>
                        <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={service.remarks}>
                            <Input.TextArea style={{height: '50px'}} onChange={onRemarksChange}/>
                        </Form.Item>
                    </EditServiceFormRow>
                </EditServiceFormLayout>
            </Form>

            <Modal open={openEditServiceParamModal} 
                onClose={()=> setOpenEditServiceParamModal(false)} 
                footer={null} 
                closable={false}
                destroyOnClose={true}
                width={600}
                >
                <EditServiceParam 
                    param={editingServiceParam}
                    onSave={(serviceParam)=> onSaveServiceParam(serviceParam)}
                    onCancel={()=> setOpenEditServiceParamModal(false)}
                    /> 
            </Modal>

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!canSave} onClick={onSave}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </EditServiceWrapper>
    )
};

interface EditServiceParamProps {
    param?: any,
    onSave: (param: any)=> void,
    onCancel: ()=> void,
};

let EditServiceParam: React.FC<EditServiceParamProps> = (props)=> {

    // form
    let [form] = Form.useForm();

    // state
    let [param, setParam] = useState<any>(props.param ?? {});
    let [canSave, setCanSave] = useState(false);

    // functions
    let validateForm = ()=> {
        setParam({...param});
        form.validateFields().then((values)=> {
            setCanSave(true);
        }).catch((err)=> {
            setCanSave(false);
        });
    };

    let onServiceNameChange = (e)=> {
        let name = e.target.value;
        param.name = name;
        validateForm();
    };

    let onServiceKeyChange = (e)=> {
        let key = e.target.value;
        param.key = key;
        validateForm();
    };

    let onDataTypeChange = (value)=> {
        param.type = value;
        validateForm();
    };

    let onMinChange = (value)=> {
        param.min = value;
        validateForm();
    };

    let onMaxChange = (value)=> {
        param.max = value;
        validateForm();
    };

    let onTrueValueChange = (e)=> {
        param.trueValue = e.target.value;
        validateForm();
    };

    let onFalseValueChange = (e)=> {
        param.falseValue = e.target.value;
        validateForm();
    };

    let onRemarksChange = (e)=> {
        let remarks = e.target.value;
        param.remarks = remarks;
        validateForm();
    };

    let onSave = ()=> {
        props.onSave(param);
    };

    let onCancel = ()=> {
        props.onCancel();
    };
    

    let isNumber = (type)=> {
        if (type == 'INT32' || type == 'INT64') {
            return true;
        }
        if (type == 'FLOAT32' || type == 'FLOAT64') {
            return true;
        }
        return false;
    };

    let dataTypeOptions = [
        {label: 'INT32', value: 'INT32'},
        {label: 'INT64', value: 'INT64'},
        {label: 'FLOAT32', value: 'FLOAT32'},
        {label: 'FLOAT64', value: 'FLOAT64'},
        {label: 'BOOL', value: 'BOOL'},
    ];

    // render
    return <EditServiceParamWrapper>
        <TitleWrapper>
            <Title>{ props.param == null ? '新增服务参数' : '编辑服务参数'}</Title>
        </TitleWrapper>

        <Form form={form} name="form" layout="vertical">
            <EditServiceParamFormLayout>
                <EditServiceParamFormRow>
                    <Form.Item name="name" label="参数名称" rules={[{required: true}]}>
                        <Input />
                    </Form.Item>
                </EditServiceParamFormRow>

                <EditServiceParamFormRow>
                    <Form.Item name="key" label="参数标识" rules={[{required: true}]}>
                        <Input />
                    </Form.Item>
                </EditServiceParamFormRow>

                <EditServiceParamFormRow>
                    <Form.Item name="type" label="参数类型" rules={[{required: true}]}>
                        <Select
                            placeholder="请选择数值类型"
                            onChange={onDataTypeChange}
                            options={dataTypeOptions}
                        />
                    </Form.Item>
                </EditServiceParamFormRow>

                { isNumber(param.type) && <EditServiceParamFormRow>
                    <Form.Item name="min" label="最小值" rules={[{required: false}]}>
                        <InputNumber style={{width: '100%'}} onChange={onMinChange}/>
                    </Form.Item>
                    <Form.Item name="max" label="最大值" rules={[{required: false}]}>
                        <InputNumber style={{width: '100%'}} onChange={onMaxChange}/>
                    </Form.Item>
                </EditServiceParamFormRow> }

                {param.type == 'BOOL' && <EditServiceParamFormRow>
                    <Form.Item name="false" label="布尔值-0" rules={[{required: true}]}>
                        <Input placeholder="如：关" onChange={onFalseValueChange}/>
                    </Form.Item>

                    <Form.Item name="true" label="布尔值-1" rules={[{required: true}]}>
                        <Input placeholder="如：开" onChange={onTrueValueChange}/>
                    </Form.Item>
                </EditServiceParamFormRow>}

                <EditServiceParamFormRow>
                    <Form.Item name="remarks" label="备注" rules={[{required: false}]}>
                        <Input.TextArea style={{height: '50px'}} onChange={onRemarksChange}/>
                    </Form.Item>
                </EditServiceParamFormRow>
            </EditServiceParamFormLayout>
            
        </Form>

        <OperationWrapper>
            <Space size="middle">
                <Button type="primary" disabled={!canSave} onClick={onSave}>确定</Button>
                <Button type="primary" danger onClick={onCancel}>取消</Button>
            </Space>
        </OperationWrapper> 
    </EditServiceParamWrapper>
};

export default DeviceServices;

